import { makeAutoObservable, observable } from 'mobx';
import { Editor } from 'tinymce';
import { WidgetConfigurationProps } from 'controls/designer/features/widgets/widgetConfig';
import {
	DynamicDataTypeDescription,
	DynamicEntry,
	TextWidgetConfig,
} from 'controls/designer/features/widgets/textWidget/types';
import { createTinymceEditor } from 'vendor-init/tinymce';
import { newGuid } from 'tools/guid';
import { i18n } from 'core/localization';
import { moveCursorRelativeToSpan } from 'controls/designer/features/widgets/textWidget/components/text-widget-editor/utils/moveCursorRelativeToSpan';
import { insertParagraphAfterSpan } from 'controls/designer/features/widgets/textWidget/components/text-widget-editor/utils/insertParagraphAfterSpan';
import { isAtRangeStart } from 'controls/designer/features/widgets/textWidget/components/text-widget-editor/utils/isAtRangeEdge';
import { isAtContentStart } from 'controls/designer/features/widgets/textWidget/components/text-widget-editor/utils/isAtContentStart';
import { isAtContentEnd } from 'controls/designer/features/widgets/textWidget/components/text-widget-editor/utils/isAtContentEnd';
import { insertSpanAtTheCursor } from 'controls/designer/features/widgets/textWidget/components/text-widget-editor/utils/insertSpanAtTheCursor';
import { getPrecedingDynamicSpan } from 'controls/designer/features/widgets/textWidget/components/text-widget-editor/utils/getPrecedingDynamicSpan';
import { getFollowingDynamicSpan } from 'controls/designer/features/widgets/textWidget/components/text-widget-editor/utils/getFollowingDynamicSpan';
import { removeSpanAndPlaceCursor } from 'controls/designer/features/widgets/textWidget/components/text-widget-editor/utils/removeSpanAndPlaceCursor';
import { getDynamicSpanAtCursor } from 'controls/designer/features/widgets/textWidget/components/text-widget-editor/utils/getDynamicSpanAtCursor';
import { expandRangeToFullTokens } from 'controls/designer/features/widgets/textWidget/components/text-widget-editor/utils/expandRangeToFullTokens';
import { regeneratePastedDynamicEntryIds } from 'controls/designer/features/widgets/textWidget/components/text-widget-editor/utils/regeneratePastedDynamicEntryIds';
import {
	getAllDynamicDataTypes,
	getDynamicDataType,
} from 'controls/designer/features/widgets/textWidget/utils/getAllDynamicDataTypes';

export class TextWidgetEditorStore {
	props: WidgetConfigurationProps<TextWidgetConfig>;
	textEditor: Editor;

	constructor() {
		makeAutoObservable(this);
	}

	createTextEditor = (x: HTMLDivElement) => {
		if (!x) {
			this.textEditor?.destroy();
			return;
		}

		requestIdleCallback(async () => {
			const initialValue = this.props.config.text;
			const dynamicTypes = getAllDynamicDataTypes();

			this.props.setFormConfigUpdater?.(this.formConfigUpdater);

			const editors = await createTinymceEditor({
				target: x,
				min_height: 200,
				setup: (ed: Editor) => {
					ed.on('Paste Change input Undo Redo NodeChange', () => {
						this.props.config.text = ed.getContent();
					});

					ed.on('click', (e: MouseEvent) => {
						const target = e.target as HTMLElement;
						if (target.hasAttribute?.('data-id')) {
							this.editDynamicEntryBySpan(target);
						}
					});

					ed.on('mouseover', (e: MouseEvent) => {
						const target = e.target as HTMLElement;
						if (target.hasAttribute?.('data-id')) {
							void this.setTooltipForSpan(target);
						}
					});

					ed.on('PastePostProcess', (e: { node: HTMLElement }) => {
						this.props.config.dynamicEntries ??= [];
						regeneratePastedDynamicEntryIds(e.node, this.props.config.dynamicEntries);
					});

					ed.on('keydown', (e) => this.onKeyDown(ed, e));

					ed.on('SelectionChange', () => this.onSelectionChange(ed));

					dynamicTypes.forEach((desc) => {
						const itemId = `dynamic_${desc.type.replace(/-/g, '_')}`;
						ed.ui.registry.addMenuItem(itemId, {
							text: desc.menuLabel,
							onAction: () => this.openDynamicDataModal(desc),
						});
					});
				},
				height: 400,
				menu: {
					dynamicdata: {
						title: i18n('Dynamic data'),
						items: dynamicTypes.map((d) => `dynamic_${d.type.replace(/-/g, '_')}`).join(' '),
					},
				},
				toolbar:
					'undo redo | styleselect | bold italic | forecolor backcolor | link | numlist bullist | alignleft aligncenter alignright alignjustify | outdent indent',
				toolbar_mode: 'wrap',
				menubar: 'edit view format dynamicdata table help',
				contextmenu: 'link table dynamicedit',
				statusbar: false,
			});

			this.textEditor = editors[0];
			this.textEditor.setContent(initialValue ?? '');
		});
	};

	onKeyDown = (ed: Editor, e: KeyboardEvent) => {
		// When cursor is inside (or right next to) a dsv span, block text input and provide
		// escape routes via Backspace/Delete. Arrow keys navigate naturally.
		// Formatting applied via menus/toolbar is unaffected (non-collapsed selections pass through).

		if (e.ctrlKey || e.metaKey || e.altKey) return;
		if (!ed.selection.isCollapsed()) return;

		const selectionRng = ed.selection.getRng();
		const dynamicSpan = getDynamicSpanAtCursor(ed, selectionRng);

		// Backspace/Delete act on the span whether the cursor is inside it, or merely sitting
		// right next to it (getParent won't match a node that's only adjacent to the span) -
		// remove the span instead of eating its first/last character.
		if (e.key === 'Backspace' || e.key === 'Delete') {
			const span =
				dynamicSpan ??
				(e.key === 'Backspace' ? getPrecedingDynamicSpan(selectionRng) : getFollowingDynamicSpan(selectionRng));
			if (span) {
				e.preventDefault();
				removeSpanAndPlaceCursor(ed, span);
			}
			return;
		}

		if (dynamicSpan) {
			if (e.key === 'Enter') {
				e.preventDefault();
				insertParagraphAfterSpan(ed, dynamicSpan);
			} else if (e.key.length === 1) {
				e.preventDefault();
				// The browser may report the cursor as being inside the span, at its very
				// start (before any of its text), when it's actually positioned right before
				// the span visually. In that case the text should land before the span, not after it.
				const isAtSpanStart = isAtRangeStart(selectionRng, dynamicSpan);
				moveCursorRelativeToSpan(ed, dynamicSpan, !isAtSpanStart);
				ed.execCommand('mceInsertContent', false, ed.dom.encode(e.key));
			}
			return;
		}

		// Cursor isn't near a dsv span at all. At the very edge of the whole editor content
		// there's no text node for the range to normalise into, so TinyMCE's default handling
		// can still land the keystroke inside a neighbouring dsv span. Give the cursor a
		// dedicated empty span to type into instead.
		if (e.key.length === 1 && (isAtContentStart(ed, selectionRng) || isAtContentEnd(ed, selectionRng))) {
			e.preventDefault();
			insertSpanAtTheCursor(ed, selectionRng);
			ed.execCommand('mceInsertContent', false, ed.dom.encode(e.key));
		}
	};

	onSelectionChange = (ed: Editor) => {
		// As soon as a selection covers part of a dsv span (mouse drag, shift+arrow, double-click,
		// etc.), grow it to cover the span in full, so the user can never end up selecting - and
		// then editing, deleting or dragging - only a fragment of the span's markup.
		if (ed.selection.isCollapsed()) return;

		const rng = ed.selection.getRng();
		const expanded = expandRangeToFullTokens(ed, rng);
		if (expanded === rng) return;

		// Range always normalises to start <= end, but a drag/shift-selection can run in
		// either direction - recover which side is the actual anchor before rewriting the
		// selection. Otherwise setRng() always re-anchors at the range's start, severing the
		// still-in-progress native selection from its real anchor: as the drag continues past
		// this point, the browser extends from the wrong end and the rest of the selection
		// (the part beyond the token, on the anchor's side) vanishes.
		const sel = ed.selection.getSel();
		const isBackward = !!sel && sel.anchorNode === rng.endContainer && sel.anchorOffset === rng.endOffset;
		ed.selection.setRng(expanded, !isBackward);
	};

	// Registered via setFormConfigUpdater, run right before the widget config is saved.
	formConfigUpdater = () => {
		this.syncDynamicEntries(this.textEditor);
	};

	private syncDynamicEntries(ed: Editor) {
		const entries = this.props.config.dynamicEntries;
		if (!entries?.length) return;
		const spans = ed.dom.select('[data-id]');
		const activeIds = new Set(spans.map((s) => s.getAttribute('data-id')));
		for (let i = entries.length - 1; i >= 0; i--) {
			if (!activeIds.has(entries[i].id)) {
				entries.splice(i, 1);
			}
		}
	}

	editDynamicEntryBySpan = (span: HTMLElement) => {
		const id = span.getAttribute('data-id');
		const entry = this.props.config.dynamicEntries?.find((e) => e.id === id);
		if (!entry) return;
		const desc = getDynamicDataType(entry.type);
		if (desc) {
			void this.openDynamicDataModal(desc, entry);
		}
	};

	setTooltipForSpan = async (span: HTMLElement) => {
		const id = span.getAttribute('data-id');
		const entry = this.props.config.dynamicEntries?.find((e) => e.id === id);
		const tooltip = entry && (await getDynamicDataType(entry.type)?.getTooltip?.(entry));
		if (tooltip) {
			span.title = tooltip;
		}
	};

	openDynamicDataModal = async (description: DynamicDataTypeDescription, existingEntry?: DynamicEntry) => {
		this.props.config.dynamicEntries ??= [];

		const isEditing = existingEntry != null;

		const draft = observable(
			existingEntry
				? (JSON.parse(JSON.stringify(existingEntry)) as DynamicEntry)
				: { type: description.type, ...description.getDefaultConfig() },
		) as DynamicEntry;

		const saved = await description.editConfig(draft);
		if (!saved) return;

		if (!draft.id) {
			draft.id = newGuid();
		}

		if (isEditing) {
			this.updateDynamicEntry(draft);
		} else {
			this.insertDynamicEntry(draft);
		}
	};

	insertDynamicEntry = (entry: DynamicEntry) => {
		this.textEditor.insertContent(`<span data-id="${entry.id}" style="color: #1677ff;">{${entry.placeholder}}</span>`);
		this.props.config.dynamicEntries.push(entry);
	};

	updateDynamicEntry = (entry: DynamicEntry) => {
		const index = this.props.config.dynamicEntries.findIndex((e) => e.id === entry.id);
		if (index !== -1) {
			Object.assign(this.props.config.dynamicEntries[index], entry);
		}

		const span = this.textEditor.dom.select(`[data-id="${entry.id}"]`)[0];
		if (span) {
			span.textContent = `{${entry.placeholder}}`;
			this.props.config.text = this.textEditor.getContent();
		}
	};

	destroy() {
		this.textEditor?.destroy();
	}
}
